import React from "react";
import { formatPaymentMethod } from "../../helpers/accountsReceivable";
import { DeskPanel } from "../desktop/DeskTable";
import "../materials/materialRecord.css";
import {
  formatMoneyFromCents,
  formatSettlementLabel,
  isReversalPayment,
  reversedPaymentIds,
  summarizeInvoiceSettlement,
  toCents,
} from "../../helpers/invoiceMoney";

function formatPaymentDate(value) {
  if (!value) return "—";
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return "—";
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function paymentBadge(payment, reversedIds) {
  if (isReversalPayment(payment)) return "Reversal";
  if (reversedIds.has(payment.payment_id)) return "Reversed";
  if (payment.origin === "backfill") return "Backfilled";
  return "";
}

export function InvoicePaymentsSection({
  invoice,
  overdue = false,
  canReverse = false,
  saving = false,
  onReverse,
  variant = "mobile",
}) {
  const settlement = summarizeInvoiceSettlement(invoice);
  const reversedIds = reversedPaymentIds(settlement.payments);
  const rows = [...settlement.payments].sort((left, right) => {
    const date = String(left.payment_date || "").localeCompare(String(right.payment_date || ""));
    if (date !== 0) return date;
    return String(left.created_at || "").localeCompare(String(right.created_at || ""));
  });
  const stateLabel = formatSettlementLabel(settlement.state, overdue);

  const body = (
    <>
      <div className="invoice-payments__totals">
        <div>
          <span>Invoice Total</span>
          <strong>{formatMoneyFromCents(settlement.totalCents)}</strong>
        </div>
        <div>
          <span>Paid</span>
          <strong>{formatMoneyFromCents(settlement.paidCents)}</strong>
        </div>
        <div>
          <span>Balance</span>
          <strong>{formatMoneyFromCents(settlement.outstandingCents)}</strong>
        </div>
      </div>
      <p className="invoice-payments__state">{stateLabel}</p>
      {rows.length === 0 ? (
        <p className="invoice-payments__empty">No payments recorded.</p>
      ) : variant === "desktop" ? (
        <div className="invoice-payments__table-wrap">
          <table className="invoice-payments__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Amount</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((payment) => {
                const badge = paymentBadge(payment, reversedIds);
                const reversed = reversedIds.has(payment.payment_id);
                const canReverseRow =
                  canReverse &&
                  !isReversalPayment(payment) &&
                  !reversed;
                return (
                  <tr
                    key={payment.payment_id}
                    className={reversed || isReversalPayment(payment) ? "is-reversed" : undefined}
                  >
                    <td>{formatPaymentDate(payment.payment_date)}</td>
                    <td>
                      {formatPaymentMethod(payment.payment_method)}
                      {badge ? <span className="invoice-payments__badge">{badge}</span> : null}
                    </td>
                    <td>{payment.payment_reference || "—"}</td>
                    <td>{formatMoneyFromCents(toCents(payment.amount))}</td>
                    <td>
                      {canReverseRow ? (
                        <button
                          type="button"
                          className="rec-btn rec-btn--danger"
                          disabled={saving}
                          onClick={() => onReverse?.(payment)}
                        >
                          Reverse
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="invoice-payments__cards">
          {rows.map((payment) => {
            const badge = paymentBadge(payment, reversedIds);
            const reversed = reversedIds.has(payment.payment_id);
            const reversal = isReversalPayment(payment);
            const canReverseRow = canReverse && !reversal && !reversed;
            return (
              <article
                key={payment.payment_id}
                className={`invoice-payments__card${reversed || reversal ? " is-reversed" : ""}`}
              >
                <div>
                  <div className="invoice-payments__card-type">
                    {reversal ? "Payment reversal" : "Payment"}
                    {badge && badge !== "Reversal" ? (
                      <span className="invoice-payments__badge">{badge}</span>
                    ) : null}
                  </div>
                  <div>{formatPaymentDate(payment.payment_date)}</div>
                  <div className="invoice-payments__card-meta">
                    {formatPaymentMethod(payment.payment_method)}
                    {payment.payment_reference ? ` · ${payment.payment_reference}` : ""}
                  </div>
                </div>
                <div className="invoice-payments__card-amount">
                  <strong className={reversal ? "is-charge" : "is-payment"}>
                    {reversal ? "+" : "−"}
                    {formatMoneyFromCents(toCents(payment.amount)).replace(/^-/, "")}
                  </strong>
                  {canReverseRow ? (
                    <button
                      type="button"
                      className="rec-btn rec-btn--danger"
                      disabled={saving}
                      onClick={() => onReverse?.(payment)}
                    >
                      Reverse
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </>
  );

  if (variant === "desktop") {
    return <DeskPanel title="Payments">{body}</DeskPanel>;
  }

  return (
    <section className="invoice-payments">
      <div className="invoice-payments__title">Payments</div>
      {body}
    </section>
  );
}
